import React, { useState, useEffect } from 'react';
import {
  TextField,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableFooter,
  TablePagination,
  Menu,
  MenuItem,
  InputAdornment,
} from '@material-ui/core';
import SearchIcon from '@material-ui/icons/Search';
import { GROUP_CREATE, GROUP_EDIT, GROUP_VIEW } from '../../../../constants/PropertyConst';
import './Group-List.scss';
import { useSelector, useDispatch } from 'react-redux';
import { allGroupsSelector, fetchAllProperties } from '../../propertySlice';
import { deleteGroupById } from '../../../../utils/PropertyAPIUtils';
import { notificationManager } from '../../../../utils/NotificationUtils';
import CreateIcon from '@material-ui/icons/Create';
import DeleteForeverIcon from '@material-ui/icons/DeleteForever';
import DeletePopupModel from '../../../../shared/delete-popup/Delete-Popup';
import TablePaginationActions from '../../../../shared/table-pagination/TablePaginationActions';
import { AddCircle, MoreHoriz } from '@material-ui/icons';
import {
  RBAC_PERMISSION_LEVEL_FULL_ACCESS,
  RBAC_TYPE_GROUPS,
} from '../../../../constants/RoleConsts';
import VisibilityIcon from '@mui/icons-material/Visibility';

const GroupList = (props) => {
  const {
    onDrawerToggle,
    onViewToggle,
    permissions,
    page,
    rowsPerPage,
    onsetRowsPerPage,
    onsetPage,
    isPropertyAdmin = false,
  } = props;
  const dispatch = useDispatch();
  const [selectedValue, setSelectedValue] = useState({
    id: '',
    name: '',
    title: '',
    description: '',
    status: false,
    anchorEl: null,
  });
  const originalGroups = useSelector(allGroupsSelector);
  const [groups, setGroups] = useState([]);
  const [tempSearchText, setTempSearchText] = useState('');
  const [searchButton, setSearchButton] = useState(true);

  useEffect(() => {
    if (originalGroups.length) {
      setGroups(originalGroups);
      setTempSearchText('');
      setSearchButton(true);
    }
  }, [originalGroups]);

  useEffect(() => {
    if (groups?.length === 0) return;
    const totalItems = groups?.length;
    const maxPageIndex = Math.max(Math.ceil(totalItems / rowsPerPage) - 1, 0);
    if (page > maxPageIndex) {
      onsetPage(maxPageIndex);
    }
  }, [groups?.length, rowsPerPage, page, onsetPage]);

  const setSearchText = (searchText) => {
    setTempSearchText(searchText);
    const filteredRows = originalGroups.filter((item) => {
      return (
        item.name.toLowerCase().includes(searchText.toLowerCase()) ||
        item.code.toLowerCase().includes(searchText.toLowerCase())
      );
    });
    onsetPage(0);
    setGroups(filteredRows);
  };

  const handleChangePage = (event, newPage) => {
    onsetPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    onsetRowsPerPage(parseInt(event.target.value, 10));
    onsetPage(0);
  };

  const onOpenDrawer = (type) => {
    onDrawerToggle(type, true, selectedValue.group);
    handleClose();
  };

  const deleteGroup = async () => {
    handleClose();
    try {
      await deleteGroupById(selectedValue.id);
      dispatch(fetchAllProperties());
      notificationManager('success', 'Group deleted successfully');
      setSelectedValue({
        id: '',
        name: '',
        title: '',
        description: '',
        status: false,
        anchorEl: null,
        group: null,
      });
    } catch (e) {
      notificationManager(
        'danger',
        e.graphQLErrors ? e.graphQLErrors[0].message : 'Group deletion failed',
      );
    }
  };

  const handleCellMenuClick = (event, group) => {
    setSelectedValue({
      id: group.id,
      name: group.name,
      title: '',
      description: '',
      status: false,
      anchorEl: event.currentTarget,
      group: group,
    });
  };

  const handleClose = () => {
    setSelectedValue({
      id: '',
      name: '',
      title: '',
      description: '',
      status: false,
      anchorEl: null,
      group: null,
    });
  };

  const handleDeletePopupOpen = () => {
    handleClose();
    setSelectedValue({
      id: selectedValue.id,
      name: selectedValue.name,
      title: 'Are you sure you want to delete',
      description:
        'This action cannot be reversed. Properties associated with this group will be lost.',
      status: true,
      anchorEl: null,
    });
  };

  const onDeleteCancel = () => {
    setSelectedValue({
      id: '',
      name: '',
      title: '',
      description: '',
      status: false,
      anchorEl: null,
    });
  };

  const onViewDetails = (group, e) => {
    if (e.target.className === 'MuiTableCell-root MuiTableCell-body') {
      onViewToggle(GROUP_VIEW, true, group);
    }
  };

  const onClickView = (e) => {
    if (selectedValue && selectedValue.group) {
      onViewToggle(GROUP_VIEW, true, selectedValue.group);
    }
  };

  return (
    <div className='mt-4 mb-4 group-list-container'>
      {selectedValue.id && selectedValue.status === true && (
        <DeletePopupModel
          deletePopUp={selectedValue}
          onDelete={deleteGroup}
          onDeleteCancel={onDeleteCancel}
        />
      )}
      <div className='row mx-2 d-flex'>
        <div className='col col-lg-4'>
          <TextField
            className='w-100'
            onChange={(e) => {
              setSearchText(e.target.value);
              setSearchButton(e?.target?.value ? false : true);
            }}
            value={tempSearchText}
            label='Search'
            InputProps={
              searchButton && {
                endAdornment: (
                  <InputAdornment position='end'>
                    <IconButton>
                      <SearchIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }
            }
          />
        </div>
        <div className='col col-lg-8 pt-3 pt-lg-0 d-flex justify-content-end'>
          {permissions[RBAC_TYPE_GROUPS] === RBAC_PERMISSION_LEVEL_FULL_ACCESS &&
            !isPropertyAdmin && (
              <Button
                variant='contained'
                color='primary'
                size='large'
                startIcon={<AddCircle />}
                onClick={(e) => {
                  onOpenDrawer(GROUP_CREATE);
                }}
              >
                Add Group
              </Button>
            )}
        </div>
      </div>
      <div className='row mx-2 d-flex mt-4'>
        <TableContainer>
          <Table className='list-table scroll-styled' aria-label='simple table'>
            <TableHead>
              <TableRow>
                <TableCell className='w-30'>Group Name</TableCell>
                <TableCell className='w-20'>Group Code</TableCell>
                <TableCell className='w-40'>Group Email Id</TableCell>
                <TableCell className='w-10'></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(rowsPerPage > 0
                ? groups.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                : groups
              ).map((group, i) => (
                <TableRow
                  key={i}
                  className='border-white pointer'
                  onClick={(e) => {
                    onViewDetails(group, e);
                  }}
                >
                  <TableCell>{group.name}</TableCell>
                  <TableCell>{group.code}</TableCell>
                  <TableCell>{group?.customSenderEmail ?? ''}</TableCell>
                  <TableCell align='right'>
                    {permissions[RBAC_TYPE_GROUPS] === RBAC_PERMISSION_LEVEL_FULL_ACCESS && (
                      <Button
                        aria-controls='simple-menu'
                        aria-haspopup='true'
                        onClick={(e) => {
                          handleCellMenuClick(e, group);
                        }}
                      >
                        <MoreHoriz />
                      </Button>
                    )}
                    <Menu
                      id={i}
                      anchorEl={selectedValue?.anchorEl}
                      disableRestoreFocus
                      MenuListProps={{ autoFocusItem: false }}
                      keepMounted
                      open={Boolean(selectedValue.anchorEl) && selectedValue.id === group.id}
                      onClose={handleClose}
                      className='shadow'
                    >
                      <MenuItem
                        className='pe-4'
                        onClick={(e) => {
                          onOpenDrawer(GROUP_EDIT);
                        }}
                      >
                        <CreateIcon className='me-2 action-menu-icon' />
                        Edit
                      </MenuItem>
                      <MenuItem
                        className='pe-4'
                        onClick={(e) => {
                          onClickView(e);
                        }}
                      >
                        <VisibilityIcon className='me-2 action-menu-icon' />
                        View
                      </MenuItem>
                      {!isPropertyAdmin && (
                        <MenuItem
                          className='pe-4'
                          onClick={(e) => {
                            handleDeletePopupOpen();
                          }}
                        >
                          <DeleteForeverIcon className='me-2 action-menu-icon' />
                          Delete
                        </MenuItem>
                      )}
                    </Menu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
            <TableFooter>
              <TableRow className='paginator'>
                <TablePagination
                  rowsPerPageOptions={[5, 10, 25, { label: 'All', value: -1 }]}
                  count={groups.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  SelectProps={{
                    inputProps: { 'aria-label': 'rows per page' },
                    native: true,
                  }}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  ActionsComponent={TablePaginationActions}
                />
              </TableRow>
            </TableFooter>
          </Table>
        </TableContainer>
      </div>
    </div>
  );
};

export default GroupList;
